import React, { useEffect, useMemo, useState } from 'react';
import { clientiService } from '../../services/data';
import '../../styles/entityPages.css';

const emptyForm = {
  tip_client: '',
  denumire: '',
  judet: '',
  oras: '',
  sediu: '',
  rc: '',
  cif: '',
  cont_bancar: '',
  banca: '',
  telefon: '',
  email: '',
  adresa_corespondenta: '',
  email_monitorizare: '',
  reprezentant_legal: '',
  functie_reprezentant: '',
  calitate: '',
  persoana_contact: '',
  copie_raport: '',
  observatii: '',
};

const fields = [
  { name: 'tip_client', label: 'Tip client' },
  { name: 'denumire', label: 'Denumire' },
  { name: 'judet', label: 'Județ' },
  { name: 'oras', label: 'Oraș' },
  { name: 'sediu', label: 'Sediu' },
  { name: 'rc', label: 'RC' },
  { name: 'cif', label: 'CIF' },
  { name: 'cont_bancar', label: 'Cont bancar' },
  { name: 'banca', label: 'Bancă' },
  { name: 'telefon', label: 'Telefon' },
  { name: 'email', label: 'Email', type: 'email' },
  { name: 'adresa_corespondenta', label: 'Adresă corespondență' },
  { name: 'email_monitorizare', label: 'Email monitorizare', type: 'email' },
  { name: 'reprezentant_legal', label: 'Reprezentant legal' },
  { name: 'functie_reprezentant', label: 'Funcție reprezentant' },
  { name: 'calitate', label: 'Calitate' },
  { name: 'persoana_contact', label: 'Persoană contact' },
  { name: 'copie_raport', label: 'Copie Raport', type: 'textarea', help: 'Adaugati in Copie Raport email-urile care sa primeasca raportul, separate prin ";"' },
  { name: 'observatii', label: 'Observații', type: 'textarea' },
];

const tableColumns = [
  'tip_client',
  'denumire',
  'oras',
  'telefon',
  'email',
  'persoana_contact',
];

const columnLabels = Object.fromEntries(
  fields.map((f) => [f.name, f.label])
);

const ClientiPage = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadItems = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await clientiService.list();
      setItems(data);
    } catch (requestError) {
      setError(requestError.message || 'Nu am putut încărca clienții.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return items;
    }

    return items.filter((item) =>
      [
        item.denumire,
        item.tip_client,
        item.email,
        item.telefon,
        item.cif,
        item.rc,
        item.judet,
        item.oras,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [items, search]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    try {
      if (editingId) {
        await clientiService.update(editingId, form);
      } else {
        await clientiService.create(form);
      }
      await loadItems();
      closeModal();
    } catch (requestError) {
      setError(requestError.message || 'Nu am putut salva clientul.');
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      ...emptyForm,
      ...item,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur vrei să ștergi acest client?')) {
      return;
    }

    setError('');
    try {
      await clientiService.remove(id);
      await loadItems();
      if (editingId === id) {
        resetForm();
      }
    } catch (requestError) {
      setError(requestError.message || 'Nu am putut șterge clientul.');
    }
  };

  return (
    <div className="entity-page">
      <div className="entity-page-header">
        <p>Gestionare completă date clienți: creare, editare, ștergere.</p>
      </div>

      {error && <div className="entity-error">{error}</div>}

      <div className="entity-page-toolbar">
        <button type="button" className="btn-primary" onClick={openCreateModal}>
          Adaugă client
        </button>
        <input
          className="entity-search"
          type="text"
          placeholder="Caută client..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className="entity-table-wrap">
        {loading ? (
          <div className="entity-loading">Se încarcă...</div>
        ) : (
          <table className="entity-table">
            <thead>
              <tr>
                {tableColumns.map((column) => (
                  <th key={column}>{column}</th>
                ))}
                <th>Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.id}>
                  {tableColumns.map((column) => (
                    <td key={column} data-label={columnLabels[column] || column}>{item[column] || '-'}</td>
                  ))}
                  <td>
                    <div className="table-actions">
                      <button type="button" className="btn-inline" onClick={() => handleEdit(item)}>
                        Edit
                      </button>
                      <button type="button" className="btn-inline danger" onClick={() => handleDelete(item.id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filteredItems.length && (
                <tr>
                  <td colSpan={tableColumns.length + 2} className="entity-empty">
                    Nu există date.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {isModalOpen && (
        <div className="entity-modal-overlay" onClick={closeModal}>
          <div className="entity-modal" onClick={(event) => event.stopPropagation()}>
            <div className="entity-modal-header">
              <h3>{editingId ? 'Edit client' : 'Adaugă client'}</h3>
              <button type="button" className="entity-modal-close" onClick={closeModal}>×</button>
            </div>

            <form className="entity-form entity-form-in-modal" onSubmit={handleSubmit}>
              <div className="entity-form-grid">
                {fields.map((field) => (
                  <div className={`entity-field ${field.type === 'textarea' ? 'entity-field-full' : ''}`} key={field.name}>
                    <label>{field.label}</label>
                    {field.type === 'textarea' ? (
                      <textarea
                        value={form[field.name] || ''}
                        onChange={(event) => handleChange(field.name, event.target.value)}
                        rows={3}
                      />
                    ) : (
                      <input
                        type={field.type || 'text'}
                        value={form[field.name] || ''}
                        onChange={(event) => handleChange(field.name, event.target.value)}
                      />
                    )}
                    {field.help && <small className="entity-field-help">{field.help}</small>}
                  </div>
                ))}
              </div>

              <div className="entity-form-actions">
                <button type="submit" className="btn-primary">
                  {editingId ? 'Actualizează client' : 'Adaugă client'}
                </button>
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Anulează
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientiPage;
